import React, { useCallback } from 'react';
import { message } from 'antd';
import { 
    ShareBenefitContainer, 
    HeaderContainer, 
    BodyContainer, 
    StickerContainer, 
    ButtonsContainer, 
    NormalText,
    EmailButton,
    WhatsappButton,
    CopyLinkButton,
    StyledDivider,
    DividerContainer,
    DividerText,
    BoldText,
    GreenText,
} from './style-components';
import email from '@/assets/icons/email/email.svg';
import copy from '@/assets/icons/copy/copy.svg';
import gift from '@/assets/icons/gift/gift.svg';
import ChipWithIcon from '@/components/chip-icon/chip-icon';
import giftIcon from '@/assets/icons/gift-icon/gift-icon.svg';
import heartIcon from '@/assets/icons/heart/heart.svg';
import { useUserContext } from '@/contexts/user/useUserContext';
import { BenefitAllocation } from '@/types/benefit-allocation/benefit-allocation';
interface ShareBenefitProps {
    benefit: BenefitAllocation;
}

const ShareBenefitComponent: React.FC<ShareBenefitProps> = ({ benefit }) => {
    const { user } = useUserContext();
    const [messageApi, contextHolder] = message.useMessage();

    const redeemdBenefits = String(import.meta.env.VITE_REDEEMD_BENEFITS).split(',');

    const isRedeemed = redeemdBenefits?.includes(benefit?.benefit?.reward_id.toString());

    const benefitLink = `${window.location.origin}/redeem-gift/${benefit.contract_address}`;
    
    const generateSimulatedResponse = () => {
        const randomNum = Math.floor(Math.random() * 900) + 100;
        return {
            contract_address: benefit.contract_address,
            user_main_key: user?.main_key ?? '',
            code: randomNum,
        };
    };

    const handleEmailShare = useCallback(() => {
        const simulatedResponse = generateSimulatedResponse();
        localStorage.setItem('sent-gift', JSON.stringify(simulatedResponse));
        localStorage.setItem('is-sent', 'true');
        const subject = "¡Te han compartido un beneficio!";
        const body = `Hola, te han compartido un beneficio de ${benefit.benefit?.reward?.name}. Puedes verlo en el siguiente enlace: ${benefitLink}`;
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [benefitLink, benefit.benefit?.reward?.name, benefit.contract_address]);

    const handleWhatsAppShare = useCallback(() => {
        const simulatedResponse = generateSimulatedResponse();
        localStorage.setItem('sent-gift', JSON.stringify(simulatedResponse));
        localStorage.setItem('is-sent', 'true');
        const text = `¡Hola! Te comparto este beneficio de ${benefit.benefit?.reward?.name}. Puedes verlo en el siguiente enlace: ${benefitLink}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [benefitLink, benefit.benefit?.reward?.name, benefit.contract_address]);

    const handleCopyLink = useCallback(async () => {
        try {
            const simulatedResponse = generateSimulatedResponse();
            localStorage.setItem('sent-gift', JSON.stringify(simulatedResponse));
            localStorage.setItem('is-sent', 'true');
            await navigator.clipboard.writeText(benefitLink);
            messageApi.success('¡Enlace copiado al portapapeles!');
        } catch (error) {
            console.error(error);
            messageApi.error('Error al copiar el enlace');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [benefitLink, messageApi, benefit.contract_address]);

    const renderRedeemedContent = () => (
        <>
            <HeaderContainer>
                <BoldText>
                    Adquiriste este beneficio para regalarlo
                </BoldText>
                <ChipWithIcon icon={giftIcon}text={'Redimido'} variant='success' />
            </HeaderContainer>
            <BodyContainer>
                <GreenText>
                    ¡Gracias por compartirlo!
                </GreenText>
                <NormalText>
                    Este beneficio ya ha sido redimido.
                </NormalText>
                <StickerContainer>
                    <img src={heartIcon} alt='heart'/>
                </StickerContainer>
            </BodyContainer>
        </>
    );

    const renderPendingContent = () => (
        <>
            <HeaderContainer>
                <BoldText>
                    Adquiriste este beneficio para regalarlo
                </BoldText>
                <NormalText>
                    Tu amigo aún no ha redimido este beneficio. ¡Recuerda compartir el enlace para que pueda usarlo!
                </NormalText>
                <ChipWithIcon icon={giftIcon} text={'Pendiente de redención'} variant='default'/>
            </HeaderContainer>
            <BodyContainer>
                <StickerContainer>
                    <img src={gift} alt='gift'/>
                </StickerContainer>
            </BodyContainer>
            <ButtonsContainer>
                <EmailButton onClick={handleEmailShare}>
                    Enviar por correo electrónico<img src={email} alt="email" />
                </EmailButton>
                <WhatsappButton onClick={handleWhatsAppShare}>
                    Enviar por WhatsApp
                </WhatsappButton>
                <DividerContainer>
                    <StyledDivider />
                    <DividerText>
                        o
                    </DividerText>
                    <StyledDivider />
                </DividerContainer>
                <CopyLinkButton onClick={handleCopyLink}>
                    Copiar enlace<img src={copy} alt="copy" />
                </CopyLinkButton>
            </ButtonsContainer>
        </>
    );

    return (
        <ShareBenefitContainer>
            {contextHolder}
            {isRedeemed ? renderRedeemedContent() : renderPendingContent()}
        </ShareBenefitContainer>
    );
};

export default ShareBenefitComponent;